import { useCallback, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import tw from 'twin.macro';
import { EyeSlashIcon } from '@heroicons/react/24/solid';

import { Alert, BaseModal, ButtonV2, Title } from '@restworld/ui-ds';

interface PutOfflineJobPositionConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
  isSubscriptionFeedbacksSubmitted?: boolean;
  ppuPaidUntil?: string | null;
}

function isPaidUntilRelevant(isoDate: string): boolean {
  const paidUntil = new Date(isoDate).getTime();
  const oneHourFromNow = Date.now() + 60 * 60 * 1000;
  return paidUntil > oneHourFromNow;
}

function formatPaidUntilDate(isoDate: string): string {
  const date = new Date(isoDate);
  return date.toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function PutOfflineJobPositionConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  isSubscriptionFeedbacksSubmitted = false,
  ppuPaidUntil,
}: PutOfflineJobPositionConfirmationModalProps) {
  const { t } = useTranslation('job-positions-page');

  const handleConfirm = useCallback(() => {
    onConfirm();
  }, [onConfirm]);

  const formattedPaidUntil = useMemo(
    () => (ppuPaidUntil && isPaidUntilRelevant(ppuPaidUntil) ? formatPaidUntilDate(ppuPaidUntil) : null),
    [ppuPaidUntil]
  );

  const titleEl = (
    <Title
      content={t('put_offline_confirmation_title')}
      fontSize="extraLarge"
      fontWeight="semibold"
      textColor="neutral-700"
      twStyle={tw`text-center leading-[30px] px-4 sm:px-0`}
    />
  );

  const footerEl = (
    <div tw="flex flex-col justify-center items-center gap-4 w-full">
      <ButtonV2
        onClick={handleConfirm}
        fullwidth
        loading={isLoading}
        StartIcon={EyeSlashIcon}
        variant="secondary-danger"
        disabled={isLoading}
      >
        {t('put_offline_confirmation_continue_button')}
      </ButtonV2>
      <ButtonV2 variant="tertiary" fullwidth onClick={() => onClose()} disabled={isLoading}>
        {t('put_offline_confirmation_cancel_button')}
      </ButtonV2>
    </div>
  );

  return (
    <BaseModal
      showMobileGestures
      modalStyles={[tw`w-full max-w-[640px] lg:(max-w-screen-sm)`]}
      open={isOpen}
      onClose={() => onClose()}
      title={titleEl}
      footer={footerEl}
    >
      <div tw="flex flex-col justify-center items-center gap-5 w-full text-center mt-6">
        <Title
          content={t('put_offline_confirmation_description')}
          fontSize="base"
          fontWeight="medium"
          textColor="neutral-700"
          twStyle={tw`whitespace-pre-line`}
        />
        {formattedPaidUntil && (
          <Alert variant="info" text={t('put_offline_ppu_paid_until_info', { date: formattedPaidUntil })} />
        )}
      </div>
    </BaseModal>
  );
}

export default PutOfflineJobPositionConfirmationModal;
